import {useTranslation} from "react-i18next";
import {AccountService} from "../services/AccountService";
import {RouteUtils} from "../routes/RouteUtils";
import {getTableLocalization as getTableLocalizationInternal} from "./table/localization";
import type {TableLocalization} from "../components/Table/Table";

type SupportedLocale = 'en_US' | 'fr_BE' | 'nl_BE' | 'nl_NL';

function getLanguage(): string {
    const locale = getLocale(false);
    if (locale) {
        return locale.split('_')[0];
    }
    return 'nl';
}

function getLanguageIdFromCode(languageCode: string): number | null {
    switch (languageCode) {
        case 'nl':
            return 2;
        case 'fr':
            return 5;
    }

    return null;

}

function getCurrentLanguageId(): number | null {
    const language = getLanguage();
    return getLanguageIdFromCode(language);
}

function changeToBCP47(locale: string): string {
    switch (locale) {
        case 'nl_BE':
        case 'nl-BE':
        case 'nl':
            return 'nl-BE';
        case 'nl_NL':
        case 'nl-NL':
            return 'nl-NL';
        case 'fr_BE':
        case 'fr-BE':
        case 'fr':
            return 'fr-BE';
        default:
            return 'nl-BE';
    }
}

function getLocale(isDash: boolean = false): string {
    let languageReturn: string;

    try {
        const user = AccountService.getUser();
        const language = AccountService.getLanguageLocalStorage();

        if (language?.name) {
            languageReturn = language.name;
        } else if (user?.locale) {
            languageReturn = user.locale;
        } else {
            languageReturn = RouteUtils.getContext() === 'be' || RouteUtils.getContext() === 'ci'
                ? 'nl_BE'
                : 'nl_NL';
        }

        // Validate that the language is supported
        if (!['en_US', 'fr_BE', 'nl_BE', 'nl_NL'].includes(languageReturn)) {
            console.warn(`Unsupported language: ${languageReturn}, falling back to nl_BE`);
            languageReturn = 'nl_BE';
        }

        return isDash ? languageReturn.replace('_', '-') : languageReturn;
    } catch (error) {
        console.error('Error getting locale:', error);
        return isDash ? 'nl-BE' : 'nl_BE';
    }
}

/**
 * Get table localization based on language
 * @param language - Language code (e.g., 'fr_BE', 'nl-BE', 'en')
 * @returns TableLocalization object with translated strings
 */
function getLocalization(language: string): TableLocalization {
    return getTableLocalizationInternal(language);
}

function getLanguageById(languageId: number): string {
    switch (languageId) {
        case 2:
            return 'nl';
        case 5:
            return 'fr';
        case 3:
            return 'en';
        default:
            return 'nl';
    }
}

function getLanguageFromI18n(i18nLang: string): string {
    switch (i18nLang) {
        case 'fr_BE':
            return 'fr';
        case 'nl_BE':
            return 'nl';
        case 'en_US':
            return 'en';
        default:
            return 'nl';
    }
}

function getLocalLanguages() {
    const { t } = useTranslation();

    return [
        { value: '-1', label: t('All') },
        { value: '2', label: t('Dutch') },
        { value: '5', label: t('French') }
    ];
}

function getLanguageLabel(value: any): string {
    const { t } = useTranslation();

    // Convert to number for consistent comparison
    const numValue = typeof value === 'string' ? parseInt(value) : value;

    if (numValue === -1) return t('All');
    if (numValue === 2) return t('Dutch');
    if (numValue === 5) return t('French');

    return value ? value.toString() : "";
}

export const LanguageUtils = {
    getLocale,
    getLanguage,
    changeToBCP47,
    getLocalization,
    getLanguageIdFromCode,
    getCurrentLanguageId,
    getLanguageById,
    getLanguageFromI18n,
    getLocalLanguages,
    getLanguageLabel,
}